import i18next, { type i18n as I18nextInstance } from "i18next";
import { catalogOf, loadCatalog } from "./catalog";
import { formatNumber } from "./format";
import { DEFAULT_LOCALE, FALLBACK_LOCALE, NAMESPACES, type Locale, type Namespace } from "./locales";

/**
 * i18next 인스턴스 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * 번역 **해석 엔진은 i18next**입니다. 조회·이름공간·en 폴백·재렌더 신호를 전부
 * i18next와 react-i18next가 담당하고, 이 파일은 그 인스턴스를 이 제품의 계약에
 * 맞게 구성합니다.
 *
 * 구성 이유
 * - `keySeparator`/`nsSeparator`를 끕니다. 메시지 ID가 `nav.route.pod-detail`처럼
 *   점을 포함하는 **평평한 안정 식별자**라, 켜 두면 i18next가 점을 중첩 경로로
 *   해석해 조회가 어긋납니다.
 * - 보간 구분자는 `{`/`}`입니다. 카탈로그가 그 형식이고, 값이 없는 자리는
 *   원문 그대로 남깁니다 — 문장이 조용히 잘리는 것보다 낫습니다.
 * - `escapeValue: false` — React가 이미 텍스트 노드를 이스케이프합니다.
 * - 백엔드 플러그인을 쓰지 않습니다. 조각은 `catalog.ts`의 리터럴 지정자 표에서
 *   오고(언어 × 이름공간 chunk), 도착한 것만 `addResourceBundle`로 넣습니다.
 *   그래서 폴링·자동 재시도·eager 로드가 없습니다.
 */
export function configureI18n(instance: I18nextInstance, locale: Locale = DEFAULT_LOCALE): I18nextInstance {
  void instance.init({
    lng: locale,
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: [...new Set([DEFAULT_LOCALE, FALLBACK_LOCALE])],
    ns: [...NAMESPACES],
    defaultNS: "common",
    fallbackNS: false,
    resources: {},
    keySeparator: false,
    nsSeparator: false,
    /* 조각은 우리가 직접 넣습니다. i18next가 스스로 무엇을 가져오지 않습니다. */
    partialBundledLanguages: true,
    initAsync: false,
    interpolation: { prefix: "{", suffix: "}", escapeValue: false },
    /* 값이 없는 자리는 지우지 않고 그대로 남깁니다. */
    missingInterpolationHandler: (_text, value) => (Array.isArray(value) ? String(value[0]) : ""),
    react: {
      /* Suspense를 쓰지 않습니다 — 조각 대기는 Provider가 결정적으로 관리합니다. */
      useSuspense: false,
      /* 조각이 추가되면 다시 그립니다. 이것이 세대 카운터 대신 쓰는 재렌더 신호입니다. */
      bindI18nStore: "added",
    },
  });
  return instance;
}

/**
 * 이 Provider가 쓰는 인스턴스를 만듭니다. 인스턴스를 분리해 두면 화면 테스트가
 * 전역 상태를 공유하지 않고, 프로덕션도 Provider 하나당 하나만 갖습니다.
 */
export function createI18n(locale: Locale = DEFAULT_LOCALE): I18nextInstance {
  return configureI18n(i18next.createInstance(), locale);
}

/** 이름공간 하나가 이 인스턴스에 이미 올라와 있는지. */
export function hasNamespace(instance: I18nextInstance, locale: Locale, namespace: Namespace): boolean {
  return instance.hasResourceBundle(locale, namespace);
}

/**
 * 조각을 받아 i18next 저장소에 올립니다.
 *
 * 이미 있으면 아무 일도 하지 않고, 같은 조각의 동시 요청은 `catalog.ts`가 하나로
 * 합칩니다. 실패는 그대로 거절로 전달되어 호출부가 폴백을 결정합니다 — 여기서
 * 삼키면 화면이 영원히 ID만 보여줍니다.
 */
export async function addNamespace(instance: I18nextInstance, locale: Locale, namespace: Namespace): Promise<void> {
  if (hasNamespace(instance, locale, namespace)) return;
  const cached = catalogOf(locale, namespace);
  const catalog = cached ?? (await loadCatalog(locale, namespace));
  /* deep=false, overwrite=false — 같은 조각을 두 번 넣어도 저장소가 커지지 않습니다. */
  instance.addResourceBundle(locale, namespace, catalog, false, false);
}

/**
 * 숫자 인자를 언어 규칙 문자열로 바꿉니다.
 *
 * i18next의 `format`은 `{value, number}`처럼 형식을 명시해야 동작합니다. 카탈로그는
 * 형식 없는 `{count}`를 쓰므로(번역자가 형식을 외우지 않아도 되게), 숫자는 여기서
 * 미리 Intl로 만들고 문자열은 **그대로** 넘깁니다 — Kubernetes 이름·queryRef·
 * reasonCode가 표기 규칙에 닿지 않아야 합니다.
 */
export function formatVars(locale: Locale, vars?: Record<string, string | number>): Record<string, string> | undefined {
  if (!vars) return undefined;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(vars)) {
    out[key] = typeof value === "number" ? formatNumber(locale, value) : value;
  }
  return out;
}
